<template>
  <div class="review-step">
    <div class="parameters-review-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Parameters Grid -->
      <div class="parameter-grid">
        <div v-for="param in parameters" :key="param.label" class="parameter-item">
          <div class="parameter-item-content">
            <span class="parameter-label">{{ param.label }}</span>
            <span
              v-if="editingParam !== param.label"
              class="parameter-value"
              @click="startEdit(param)"
              >{{ param.value }}</span
            >
            <div v-else class="edit-mode">
              <custom-input-text
                v-model="editValue"
                v-focustrap
                type="text"
                class="edit-input"
              />
              <custom-button
                @click="confirmEdit"
                class="confirm-btn"
                icon="pi pi-check"
                rounded
              />
            </div>
          </div>
          <div v-if="param.hint" class="parameter-item-hind" @click="openHint(param.hint)">
            <CustomSvgIcons name="scope" :size="16" />
            {{ param.hint.hintName }}
          </div>
        </div>
      </div>

      <!-- Edit Note -->
      <div class="edit-note">
        <p>
          <i class="pi pi-info-circle"></i>
          Если какие-то параметры нужно изменить, вернитесь к предыдущим шагам
        </p>
      </div>

      <CustomDrawer
        v-model="showHint"
        :title="hintTitle"
        :video-src="videoSrc"
        :description="hintDescription"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, shallowRef } from 'vue'
import { useSurveyModal } from '@/features/survey/store'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'
import CustomInputText from '@/components/UI/CustomInputText/CustomInputText.vue'
import CustomSvgIcons from '@/components/HelpersComponents/CustomSvgIcons.vue'
import CustomDrawer from '@/components/UI/CustomDrawer.vue'
import type { SurveyStep, MeasurementType } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'review' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()

const showHint = ref(false)
const hintTitle = ref('title')
const videoSrc = ref<string | undefined>('/video/inseam-measurement.mp4')
const hintDescription = ref('description')

interface ParameterHint {
  hintName: string
  title: string
  video_src: string | null
  description: string
}

interface ParameterItem {
  label: string
  value: string
  dataKey: string
  hint?: ParameterHint
}

const editingParam = ref<string | null>(null)
const editValue = ref('')

const formatMeasurement = (value: number | null, unit = 'см'): string => {
  if (value === null || Number.isNaN(value)) {
    return '—'
  }
  return `${value} ${unit}`
}

const getMeasurementLabel = (type: MeasurementType): string => {
  const labels: Record<MeasurementType, string> = {
    height: 'Рост:',
    weight: 'Вес:',
    chest: 'Обхват груди:',
    underChest: 'Обхват под грудью:',
    waist: 'Обхват талии:',
    footLength: 'Длина стопы:',
    shoulderWidth: 'Ширина плеч:',
    inseamLength: 'Длина от талии до пола:',
    shoulderToWaist: 'Длина от плеч до талии:',
    armLength: 'Длина руки:',
    armCircumference: 'Обхват руки:',
    shoeSize: 'Размер обуви:'
  }
  return labels[type] || ''
}

const getMeasurementUnit = (type: MeasurementType): string => {
  if (type === 'weight') return 'кг'
  if (type === 'shoeSize') return 'размер'
  return 'см'
}

// Мемоизируем steps для оптимизации
const measurementSteps = computed(() => {
  return surveyStore.steps.filter(step => step.type === 'measurement' || step.type === 'shoe_size')
})

const parameters = computed<ParameterItem[]>(() => {
  const data = surveyStore.surveyModal.surveyData
  const params: ParameterItem[] = []

  // Add gender
  if (data.gender !== null && data.gender !== undefined) {
    params.push({
      label: 'Пол:',
      value: data.gender === 'male' ? 'Мужской' : data.gender === 'female' ? 'Женский' : '—',
      dataKey: 'gender',
      hint: {
        hintName: 'Как правильно определить пол',
        title: 'Пол',
        video_src: null,
        description: 'Описание для определения пола'
      }
    })
  }

  // Add measurements from steps - используем уже отфильтрованный список
  measurementSteps.value.forEach((step) => {
    const dataKey = step.id
    const value = data[dataKey]
    if (value !== null && value !== undefined) {
      const unit = step.unit || 'см'
      const hint = step.hint
      // Используем measurementType для label, если оно доступно, иначе используем dataKey
      const measurementType = step.measurementType || (dataKey as MeasurementType)

      params.push({
        label: getMeasurementLabel(measurementType),
        value: formatMeasurement(value, unit),
        dataKey: dataKey,
        hint: hint
          ? {
              hintName: hint.title,
              title: hint.title,
              video_src: hint.video_src,
              description: hint.description
            }
          : undefined
      })
    }
  })

  return params
})

function openHint(hint: ParameterHint) {
  hintTitle.value = hint.title
  videoSrc.value = hint.video_src || undefined
  hintDescription.value = hint.description
  showHint.value = true
}

function startEdit(param: ParameterItem) {
  editingParam.value = param.label
  if (param.dataKey === 'gender') {
    editValue.value = param.value
  } else {
    editValue.value = param.value.split(' ')[0] // get number
  }
}

function confirmEdit() {
  if (!editingParam.value) return
  const data = surveyStore.surveyModal.surveyData
  const param = parameters.value.find((p) => p.label === editingParam.value)
  if (!param) return

  if (param.dataKey === 'gender') {
    data.gender = editValue.value === 'Мужской' ? 'male' : 'female'
  } else {
    const numericValue = Number.parseFloat(editValue.value)
    if (!Number.isNaN(numericValue)) {
      data[param.dataKey] = numericValue
    }
  }
  editingParam.value = null
}
</script>

<style scoped>
.review-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
  background-color: var(--grey-40);
}

.parameters-review-content {
  max-width: 600px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.parameter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.parameter-item {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: 28px;
  background: #fff;
  padding: 6px;
}

.parameter-item-hind {
  display: flex;
  align-items: center;
  color: var(--active-100);
  padding: 16px;
  gap: 12px;
  cursor: pointer;
}

.parameter-item-content {
  display: flex;
  width: 100%;
  border: 1px solid var(--primary-blue-12);
  justify-content: space-between;
  padding: 16px;
  border-radius: 28px;
}

.parameter-label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.parameter-value {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  cursor: pointer;
}

.edit-mode {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-input {
  width: 80px;
}

.edit-note {
  text-align: center;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 12px;
}

.edit-note p {
  font-size: 14px;
  color: #856404;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.edit-note i {
  color: #856404;
}

@media (max-width: 768px) {
  .parameter-grid {
    grid-template-columns: 1fr;
  }

  .parameter-item {
    gap: 8px;
  }
}
</style>

